import { ApiKey, GTagId } from '@paid-search/core/src/domain';
import { HotelGTagIds } from '@paid-search/core/src/accountHotel/HotelGTagIds';
import GenericFetch, { GenericFetchResponse } from '@paid-search/core/src/config/GenericFetch';
import { asError } from 'catch-unknown';
import { NestedError } from './NestedError';
import { JSONableError } from '@paid-search/core/src/error';

export class HotelGTagIdsInPublicApi implements HotelGTagIds {
  constructor(private readonly publicApiEndpoint: string, private readonly httpHandler: GenericFetch) {}

  async findAll(apiKey: ApiKey): Promise<GTagId[]> {
    const uri = `${this.publicApiEndpoint}/apiKeys/${apiKey.value}/gtags`;

    let response: GenericFetchResponse;
    try {
      response = await this.httpHandler(uri);
    } catch (e) {
      throw new UnableToFindGTagIdsForAnApiKey(uri, apiKey, asError(e));
    }

    if (response.status >= 200 && response.status < 300) {
      const json = await response.json();

      if (json.gtags) {
        const gtags = (json.gtags as []).map((gtag) => new GTagId(gtag));

        return gtags;
      } else {
        throw new Error(`Public API responded with unknown response ${response.status}: ${json}`);
      }
    } else if (response.status === 404) {
      return [];
    } else {
      throw new Error(`Public API responded with ${response.status}: ${await response.text()}`);
    }
  }
}

export class UnableToFindGTagIdsForAnApiKey extends JSONableError implements NestedError {
  // @ts-ignore
  // noinspection JSUnusedLocalSymbols
  private readonly _b623495112b94552955fb6c50f3ae23b_Brand = 'Prevent structural compatibility';
  public readonly name: string;

  constructor(public readonly path: string, public readonly apiKey: ApiKey, readonly innerError: Error) {
    super();
    this.message = `Unable to get GTag IDs`;
    this.name = 'UnableToFindGTagIdsForAnApiKey';
  }

  toJSON() {
    return {
      ...super.toJSON(),
      message: this.message,
      name: this.name,
      apiKey: this.apiKey.value,
      path: this.path,
      cause: {
        message: this.innerError.message,
        stack: this.innerError.stack,
        name: this.innerError.name,
      },
    };
  }

  toString(): string {
    return JSON.stringify(this.toJSON());
  }

  isEqualTo(other: UnableToFindGTagIdsForAnApiKey): boolean {
    return this.path === other.path && this.apiKey.isEqualTo(other.apiKey) && this.innerError.message === other.innerError.message;
  }
}
